import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useIsRealtimeConnected } from '@/core/realtime';
import * as api from './api';
import type { NotificationListParams, NotificationPreferenceUpdateInput } from './types';

/** Query-key factory — every notifications key derives from here. */
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (params: NotificationListParams) => [...notificationKeys.lists(), params] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
};

/**
 * Polling intervals for the badge. Realtime lengthens the poll; it never
 * replaces it (FE-ADR-017) — the socket may be down, blocked by a proxy, or
 * simply not deployed, and the badge must still be right.
 */
export const UNREAD_POLL_MS = 60_000;
export const UNREAD_POLL_CONNECTED_MS = 5 * 60_000;

export function useNotifications(params: NotificationListParams = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => api.getNotifications(params),
  });
}

export function useUnreadCount() {
  const connected = useIsRealtimeConnected();
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: () => api.getUnreadCount(),
    refetchInterval: connected ? UNREAD_POLL_CONNECTED_MS : UNREAD_POLL_MS,
  });
}

/** Reading a notification moves the list AND the badge — never the
 *  preferences, which is why neither invalidates `notificationKeys.all`. */
function invalidateInbox(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: notificationKeys.lists() });
  void qc.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.markRead(publicId),
    onSuccess: () => invalidateInbox(qc),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.markAllRead(),
    onSuccess: () => invalidateInbox(qc),
  });
}

/** The caller's own delivery switches (a singleton, not a collection). */
export function usePreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () => api.getPreferences(),
  });
}

export function useUpdatePreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NotificationPreferenceUpdateInput) => api.updatePreferences(input),
    onSuccess: preference => qc.setQueryData(notificationKeys.preferences(), preference),
  });
}
